import type { FC } from "react";
import { Mail } from "lucide-react";

import {
  FieldInput,
  FieldRadio,
  FieldSelect,
  FieldTextArea,
} from "@/components/form";
import FieldDatepicker from "@/components/form/field-datepicker";
import FieldInputPhone from "@/components/form/field-phone";
import FormComponentTitle from "@/components/form/form-component-title";
import type { UpdateFieldProps } from "@/components/form/patient/update-form";
import { Separator } from "@/components/ui/separator";
import { updatePatientSchema } from "@/schemas/patient";
import { consultationOptions, genderOptions } from "@/utils/patient";

const InformationsForm: FC<UpdateFieldProps> = ({ formApi }) => {
  return (
    <div className="flex flex-col gap-4 w-full">
      <FormComponentTitle
        title="Informations"
        subTitle="Ajouter les informations du patient"
      />
      <Separator />

      {/* Consultation Motive */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Motif de consultation*
        </label>
        <FieldSelect
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="consultationMotive"
          options={consultationOptions}
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.consultationMotive,
          }}
        />
      </div>
      <Separator />

      {/* Gender */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Sexe*
        </label>
        <FieldRadio
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="gender"
          options={genderOptions}
          orientation="horizontal"
          validators={{
            onChange: updatePatientSchema.shape.gender,
          }}
        />
      </div>
      <Separator />

      {/* Last Name */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Nom du patient*
        </label>
        <FieldInput
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="lastName"
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.lastName,
          }}
        />
      </div>
      <Separator />

      {/* First Name */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Prénom du patient*
        </label>
        <FieldInput
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="firstName"
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.firstName,
          }}
        />
      </div>
      <Separator />

      {/* Address */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Adresse postale
        </label>
        <div className="flex flex-col gap-4 w-full lg:max-w-[512px]">
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            label="Adresse"
            name="address"
            placeholder="Adresse"
            validators={{
              onChange: updatePatientSchema.shape.address,
            }}
          />
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            label="Complément d'adresse"
            name="addressComplement"
            placeholder="Complément d'adresse"
            validators={{
              onChange: updatePatientSchema.shape.addressComplement,
            }}
          />
          <div className="flex flex-col sm:flex-row gap-4">
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              label="Code postal"
              name="postalCode"
              placeholder="Code postal"
              type="number"
              validators={{
                onChange: updatePatientSchema.shape.postalCode,
              }}
            />
            <FieldInput
              Field={formApi.Field}
              schema={updatePatientSchema}
              label="Ville"
              name="city"
              placeholder="Ville"
              validators={{
                onChange: updatePatientSchema.shape.city,
              }}
            />
          </div>
        </div>
      </div>
      <Separator />

      {/* Email */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Adresse email*
        </label>
        <div className="flex relative w-full lg:max-w-[512px]">
          <Mail className="absolute left-2 top-2 z-10 text-gray-500" />
          <FieldInput
            Field={formApi.Field}
            schema={updatePatientSchema}
            name="email"
            className="pl-9 w-full"
            validators={{
              onChange: updatePatientSchema.shape.email,
            }}
          />
        </div>
      </div>
      <Separator />

      {/* Birth Date */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Date de naissance*
        </label>
        <FieldDatepicker
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="birthDate"
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.birthDate,
          }}
        />
      </div>
      <Separator />

      {/* Home Phone */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Téléphone fixe
        </label>
        <FieldInputPhone
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="homePhone"
          placeholder="XX XX XX XX XX"
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.homePhone,
          }}
        />
      </div>
      <Separator />

      {/* Mobile Phone */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Téléphone mobile
        </label>
        <FieldInputPhone
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="mobilePhone"
          placeholder="XX XX XX XX XX"
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.mobilePhone,
          }}
        />
      </div>
      <Separator />

      {/* Job */}
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8">
        <label className="text-sm font-semibold text-gray-700 lg:w-full lg:max-w-[280px] flex-shrink-0">
          Profession
        </label>
        <FieldTextArea
          Field={formApi.Field}
          schema={updatePatientSchema}
          name="job"
          placeholder="Écrire..."
          className="w-full lg:max-w-[512px]"
          validators={{
            onChange: updatePatientSchema.shape.job,
          }}
        />
      </div>
    </div>
  );
};

export default InformationsForm;
